/**
 * shared/lib/bespoke-seed/atom-axes.ts — bespoke memory-seeding #1221, Task D2-j
 *
 * SINGLE source of truth for the bespoke atom life-axis vocabulary and the
 * non-reversible free-text feedback budget. Both tiers consume these:
 *   - the server wire contract (`bespoke-seed-digest.schema.ts` ATOM_AXIS_IDS /
 *     SIGNAL_FEEDBACK_MAX z.enum + .max), and
 *   - the client on-device boundary (`client/src/lib/api/bespoke-seed.ts`).
 *
 * Pre-D2-j these were declared as independent literal copies on each side (a
 * second `["sensory","era","domain","lived_known","other"]` list and a second
 * `140`); a drift between them would silently widen the on-device privacy
 * boundary or split the recognised axis set. Living in `shared/` (no cross-tier
 * import) is the same discipline as canonicalize.ts and @shared/lib/sufficiency.
 *
 * The RECOGNISED axis set here MUST agree with `KNOWN_AXES` + the "other"
 * catch-all in `server/logic/challenge/seed-bank.ts` (resolveAxis) and the
 * `PIVOT_AXES` rotation order in `server/logic/seed-interview/pivot.ts`. An
 * unrecognised raw axis collapsing to "other" is INTENTIONAL (resolveAxis); this
 * file fixes only the VOCAB the recognised paths agree on.
 */

/**
 * Life-axis identifiers — the closed enum every bespoke surface agrees on. The
 * four named axes plus the "other" catch-all (the resolveAxis fallback bucket).
 * Order is the wire/enum order; pivot rotation order lives in pivot.ts.
 */
export const ATOM_AXIS_IDS = ["sensory", "era", "domain", "lived_known", "other"] as const;
export type AtomAxisId = (typeof ATOM_AXIS_IDS)[number];

/**
 * Max length of any free-text feedback signal. Bounded so prose (an answer, a
 * transcript) cannot be smuggled through a wide string field (Immutable Rule
 * 5/10). Distinct from Wave-9 REPAIR_FEEDBACK_MAX (same value, different channel).
 */
export const SIGNAL_FEEDBACK_MAX = 140;

// Spec 2026-08-25 AC-14. A few words naming the subject the person already
// has in mind, so the interviewer aims there instead of guessing from the
// narrative ring. Short by design: it is an aim, not a memory, and a long one
// starts to be the answer.
export const AIM_HINT_MAX = 120;
